import * as MobX from "mobx";
import { ApiResult, BaseStore, KeyValuePair } from "@shoothill/core";
import { IGetAllLookupData, LookupDataService } from "./Services/LookupDataService";
import { EmploymentStatusEnum } from "Globals/Models/EmploymentStatusEnum";

import { SpokenLanguageModel } from "Shared";
import { DisabilityImpactModel } from "Shared/Models/DisabilityImpactModel";

/*
This store is a quick attempt at adding some structure and rigor to what otherwise is, put bluntly, a dumpster fire.
You will notice some oddities (e.g. placeholderKVP, string values instead of integers or IDs). These are simply because there is no way of implementing it correctly without causing multiple chained regression bugs.
The hope is every time there is a new feature or maintenance job, the code will become better.
This is only a hope.
The majority of the code seems beyond redemption.
*/

export class LookupStore extends BaseStore {
    private optionsDataService = new LookupDataService();

    // region Observables
    @MobX.observable public genderKeyValuePairs: KeyValuePair<string>[] = [];
    @MobX.observable public salutationKeyValuePairs: KeyValuePair<string>[] = [];
    @MobX.observable public sharedOwnershipKeyValuePairs: KeyValuePair<string>[] = [];
    @MobX.observable public employmentStatusKeyValuePairs: KeyValuePair<string>[] = [];
    @MobX.observable public spokenLanguages: SpokenLanguageModel[] = [];
    @MobX.observable public disabilityImpacts: DisabilityImpactModel[] = [];
    @MobX.observable public developmentalConditionKeyValuePairs: KeyValuePair[] = [];
    @MobX.observable public supportNeedsKeyValuePairs: KeyValuePair[] = [];
    @MobX.observable public languageBarrierKeyValuePairs: KeyValuePair[] = [];
    // endregion

    constructor() {
        super();
    }

    // region Initialisation
    @MobX.action
    public initialise = async (): Promise<void> => {
        const result: ApiResult<IGetAllLookupData> = await this.optionsDataService.getAllOptions();
        if (result.wasSuccessful) {
            this.setOptions(result.payload);
        }
    };

    @MobX.action
    private setOptions = (payload: IGetAllLookupData) => {
        this.genderKeyValuePairs = [...payload.genderKeyValuePairs];
        this.salutationKeyValuePairs = [...payload.salutationKeyValuePairs];
        this.sharedOwnershipKeyValuePairs = [...payload.sharedOwnershipKeyValuePairs];
        this.employmentStatusKeyValuePairs = [...payload.employmentStatusKeyValuePairs];
        this.spokenLanguages = [...payload.spokenLanguages];
        this.disabilityImpacts = [...payload.disabilityImpacts];
        this.developmentalConditionKeyValuePairs = [...payload.developmentalConditionKeyValuePairs];
        this.supportNeedsKeyValuePairs = [...payload.supportNeedsKeyValuePairs];
        this.languageBarrierKeyValuePairs = [...payload.languageBarrierKeyValuePairs];
    };
    // endregion

    //! this should not exist. EVER! but a simple Ctrl+Shift+F will show that the placeholder string is used everywhere instead of the standard undefined/null. in order to properly use null instead would require rewriting a lot of spaghetti code.

    public get placeholderKeyValuePair(): KeyValuePair<string> {
        return { key: "Please select", value: "placeholder" } as KeyValuePair<string>;
    }

    // region Computed
    @MobX.computed
    public get getGenderOptions(): KeyValuePair<string>[] {
        return [this.placeholderKeyValuePair, ...this.genderKeyValuePairs];
    }

    @MobX.computed
    public get getSalutationOptions(): KeyValuePair<string>[] {
        return [this.placeholderKeyValuePair, ...this.salutationKeyValuePairs];
    }

    @MobX.computed
    public get getSharedOwnershipOptions(): KeyValuePair[] {
        return this.sharedOwnershipKeyValuePairs;
    }

    @MobX.computed
    public get getEmploymentStatusOptions(): KeyValuePair[] {
        return [{ key: "Please select", value: EmploymentStatusEnum.Empty }, ...this.employmentStatusKeyValuePairs];
    }

    @MobX.computed
    public get getSpokenLanguageOptions(): KeyValuePair[] {
        return [{ key: "Please select", value: null }, ...this.spokenLanguageKVPs];
    }

    @MobX.computed
    public get getDevelopmentalConditionOptions(): KeyValuePair[] {
        return [{ key: "Please select", value: null }, ...this.developmentalConditionKeyValuePairs];
    }

    @MobX.computed
    public get getSupportNeedsOptions(): KeyValuePair[] {
        return [{ key: "Please select", value: null }, ...this.supportNeedsKeyValuePairs];
    }

    @MobX.computed
    public get getLanguageBarrierOptions(): KeyValuePair[] {
        return [{ key: "Please select", value: null }, ...this.languageBarrierKeyValuePairs];
    }

    @MobX.computed
    public get getDisabilityImpactOptions(): KeyValuePair[] {
        return this.disabilityImpactKVPs;
    }

    private get spokenLanguageKVPs() {
        return this.spokenLanguages.map((m) => {
            const kvp: KeyValuePair = {
                key: m.name,
                value: m.id,
            };
            return kvp;
        });
    }

    private get disabilityImpactKVPs() {
        return this.disabilityImpacts.map((m) => {
            const kvp: KeyValuePair = {
                key: m.name,
                value: m,
            };
            return kvp;
        });
    }
    // endregion

    public get getYearOptions(): KeyValuePair[] {
        return Array.from({ length: 100 }, (_, i) => ({
            key: String(i).concat(" ", `${i == 1 ? "year" : "years"}`),
            value: i,
        }));
    }

    public get getMonthOptions(): KeyValuePair[] {
        let months = MobX.observable<KeyValuePair>([]);
        for (let index = 0; index <= 11; index++) {
            months.push({ key: `${index} ${index == 1 ? "month" : "months"}`, value: index.toString() });
        }
        return months;
    }
}
